import { useMemo, useState } from 'react'
import { useAsync } from '../hooks/useAsync'
import { fetchRosterPlayerStats, fetchSeasons, fetchStats } from '../services/league'
import SeasonPicker from '../components/SeasonPicker'
import { TeamHelmet } from '../components/TeamHelmet'
import {
  Card,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  RowLink,
  RowLinkTarget,
  SortableHeaderRow,
  formatPct,
  formatRecord,
  inGamePlayerLogPath,
  playerProfilePath,
  recordSortKey,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import {
  ROSTER_STAT_CATEGORIES,
  STAT_CATEGORIES,
  formatRosterStatValue,
  formatStatValue,
  type RosterStatCategory,
  type StatCategory,
} from '../lib/statCategories'
import type { CoachStats, RosterPlayerStats } from '../types'

/**
 * One category's leaderboard.
 *
 * @remarks
 * Mount this with `key={category.slug}`. The active sort is component state,
 * and a category switch has to discard it: the previous category's column
 * usually still exists on {@link CoachStats} and would silently keep ordering
 * the table by a statistic no longer on screen.
 */
function StatTable({ rows, category }: { rows: CoachStats[]; category: StatCategory }) {
  const visibleRows = useMemo(
    () =>
      category.requiresExtraStats
        ? rows.filter(row => row.gamesWithExtraStats > 0)
        : rows,
    [rows, category]
  )

  const columns = useMemo<Array<SortableColumn<CoachStats>>>(
    () => [
      { key: 'rank', label: '#', className: 'rank' },
      { key: 'player', label: 'Player', sort: row => row.displayName },
      {
        key: 'record',
        label: 'Record',
        className: 'record',
        hint: 'Sort by wins, then by fewest losses',
        sort: row => recordSortKey(row.wins, row.losses),
      },
      ...category.columns.map(column => ({
        key: String(column.key),
        label: column.label,
        className: 'num',
        hint: column.hint,
        sort: (row: CoachStats) => Number(row[column.key]),
      })),
    ],
    [category]
  )

  const sort = useTableSort(visibleRows, columns, {
    key: String(category.defaultSort),
    direction: category.defaultSortDirection ?? 'desc',
  })

  if (visibleRows.length === 0) {
    if (category.requiresExtraStats) {
      return (
        <EmptyState title="No extra team stats in this scope">
          Time of possession, takeaways, and luck score start in season 34, when the v5 ROM began
          writing those counters.
        </EmptyState>
      )
    }

    return (
      <EmptyState title="No parsed game data yet">
        Stats come from Tecmo Super Bowl save states, so they appear once results are uploaded.
      </EmptyState>
    )
  }

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={columns} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map((row, index) => (
            <tr key={row.leaguePlayerId}>
              <td className="rank">{index + 1}</td>
              <td>
                <PlayerCell
                  name={row.displayName}
                  discordId={row.discordId}
                  avatar={row.discordAvatar}
                  to={playerProfilePath(row.leaguePlayerId)}
                />
              </td>
              <td className="record">{formatRecord(row.wins, row.losses, row.ties)}</td>
              {category.columns.map(column => (
                <td key={String(column.key)} className="num">
                  {formatStatValue(column, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * One in-game player leaderboard.
 *
 * @param seasonScope - The season the page is showing, carried into each row's
 * game log so a leader clicked from season 33 opens season 33 rather than
 * quietly widening to every year and showing a log that cannot add up to the
 * row just clicked.
 *
 * @remarks
 * Mount this with `key={category.slug}` for the same reason {@link StatTable}
 * needs it — a stale sort would otherwise survive a category switch.
 *
 * Rows arrive covering every category at once, so this filters to the players
 * the category actually applies to. That filter is the difference between a
 * passing board of quarterbacks and a passing board of every lineman on every
 * team anyone used.
 */
function RosterStatTable({
  rows,
  category,
  seasonScope,
}: {
  rows: RosterPlayerStats[]
  category: RosterStatCategory
  seasonScope?: number | 'all'
}) {
  const qualified = useMemo(() => rows.filter(category.qualifies), [rows, category])

  const columns = useMemo<Array<SortableColumn<RosterPlayerStats>>>(
    () => [
      { key: 'rank', label: '#', className: 'rank' },
      { key: 'player', label: 'In-game player', sort: row => row.name },
      { key: 'position', label: 'Pos', sort: row => row.position },
      {
        key: 'gamesPlayed',
        label: 'GP',
        className: 'num',
        hint: 'Games with a box score in which this team was used',
        sort: row => row.gamesPlayed,
      },
      ...category.columns.map(column => ({
        key: String(column.key),
        label: column.label,
        className: 'num',
        sort: (row: RosterPlayerStats) => Number(row[column.key]),
      })),
    ],
    [category]
  )

  const sort = useTableSort(qualified, columns, {
    key: String(category.defaultSort),
    direction: 'desc',
  })

  if (rows.length === 0) {
    return (
      <EmptyState title="No parsed game data yet">
        In-game player stats come from Tecmo Super Bowl save states, which the league started
        uploading in season 33. Pick season 33 or later, or all seasons.
      </EmptyState>
    )
  }

  if (qualified.length === 0) {
    return (
      <EmptyState title={`No ${category.title.toLowerCase()} in this scope`}>
        The box scores here record nothing for any in-game player in this category. Pick another
        season, or all seasons.
      </EmptyState>
    )
  }

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={columns} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map((row, index) => {
            const logPath = inGamePlayerLogPath(row.teamAbbreviation, row.name, seasonScope)

            return (
              <RowLink key={row.id} to={logPath}>
                <td className="rank">{index + 1}</td>
                <td>
                  <div className="player">
                    <TeamHelmet abbreviation={row.teamAbbreviation} size="sm" />
                    <RowLinkTarget
                      to={logPath}
                      label={`${row.name} game log — every game behind this row`}
                    >
                      <span className="player__name">{row.name}</span>
                    </RowLinkTarget>
                    <span className="player__team">
                      <span className="team-tag">{row.teamAbbreviation}</span>
                    </span>
                  </div>
                </td>
                <td>{row.position}</td>
                <td className="num">{row.gamesPlayed}</td>
                {category.columns.map(column => (
                  <td key={String(column.key)} className="num">
                    {formatRosterStatValue(column, row)}
                  </td>
                ))}
              </RowLink>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function Stats() {
  const seasons = useAsync(fetchSeasons, [])
  const [selectedSeason, setSelectedSeason] = useState<number | 'all' | null>(null)
  const [activeCategory, setActiveCategory] = useState(STAT_CATEGORIES[0].slug)
  const [activeRosterCategory, setActiveRosterCategory] = useState(
    ROSTER_STAT_CATEGORIES[0].slug
  )

  const currentSeason = seasons.data?.find(season => season.isCurrent)
  const seasonValue = selectedSeason ?? currentSeason?.number

  const stats = useAsync(() => fetchStats(seasonValue), [seasonValue])
  const rosterStats = useAsync(() => fetchRosterPlayerStats(seasonValue), [seasonValue])
  const category =
    STAT_CATEGORIES.find(item => item.slug === activeCategory) ?? STAT_CATEGORIES[0]
  const rosterCategory =
    ROSTER_STAT_CATEGORIES.find(item => item.slug === activeRosterCategory) ??
    ROSTER_STAT_CATEGORIES[0]

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Stats</span>
          <h1>Leaderboards</h1>
          <p>
            These stats come from save state files. Uploading save states started in season 33, so
            points scored and allowed are the only stats on record before then. Time of
            possession, takeaways, and luck score start in season 34.
          </p>
        </div>
        {seasons.data && seasons.data.length > 0 && (
          <SeasonPicker
            seasons={seasons.data}
            value={seasonValue ?? 'all'}
            onChange={setSelectedSeason}
            includeAll
          />
        )}
      </header>

      {stats.error && <Notice tone="error">{stats.error}</Notice>}

      <div className="row">
        {STAT_CATEGORIES.map(item => (
          <button
            key={item.slug}
            type="button"
            className={item.slug === category.slug ? 'btn btn--accent btn--sm' : 'btn btn--sm'}
            onClick={() => setActiveCategory(item.slug)}
          >
            {item.title}
          </button>
        ))}
      </div>

      <Card title={category.title} subtitle={category.description} flush>
        {stats.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={6} />
          </div>
        ) : (
          <StatTable key={category.slug} rows={stats.data ?? []} category={category} />
        )}
      </Card>

      {stats.data &&
        stats.data.length > 0 &&
        (!category.requiresExtraStats ||
          stats.data.some(row => row.gamesWithExtraStats > 0)) && (
          <p className="muted" style={{ fontSize: '0.82rem' }}>
            Leaders sorted by {category.columns.find(c => c.key === category.defaultSort)?.label}.
            Click any column heading to re-sort. League-best win rate:{' '}
            {formatPct(Math.max(...stats.data.map(row => row.winPercentage)))}.
          </p>
        )}

      <header className="page-header">
        <div>
          <span className="eyebrow">In-game players</span>
          <h2>In-game player leaders</h2>
          <p>
            The NES players on the Tecmo rosters, not the league members who control them. Every
            row pools the whole league: if three players used San Diego, Marion Butts&rsquo; carries
            for all three add up here. Click a row to see the games behind it, and who was
            controlling him in each.
          </p>
        </div>
      </header>

      {rosterStats.error && <Notice tone="error">{rosterStats.error}</Notice>}

      <div className="row">
        {ROSTER_STAT_CATEGORIES.map(item => (
          <button
            key={item.slug}
            type="button"
            className={
              item.slug === activeRosterCategory ? 'btn btn--accent btn--sm' : 'btn btn--sm'
            }
            onClick={() => setActiveRosterCategory(item.slug)}
          >
            {item.title}
          </button>
        ))}
      </div>

      <Card title={rosterCategory.title} subtitle={rosterCategory.description} flush>
        {rosterStats.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={6} />
          </div>
        ) : (
          <RosterStatTable
            key={rosterCategory.slug}
            rows={rosterStats.data ?? []}
            category={rosterCategory}
            seasonScope={seasonValue}
          />
        )}
      </Card>

      {rosterStats.data && rosterStats.data.length > 0 && (
        <p className="muted" style={{ fontSize: '0.82rem' }}>
          In-game players sorted by{' '}
          {rosterCategory.columns.find(c => c.key === rosterCategory.defaultSort)?.label}, a volume
          figure — a season is only a handful of games, so opening on a rate would rank a
          one-throw passer first. Click any column heading to re-sort.
        </p>
      )}
    </div>
  )
}
